<template>
	<div class="menu-view-container vld-parent">
		<EventMenu v-if="menuToShow == menuComponents.EVENT" />
		<ResidentialMenu v-else-if="menuToShow == menuComponents.RESIDENTIAL" />
		<HotelMenu
			v-else-if="menuToShow == menuComponents.HOTEL"
			:menuTabs="menuTabs"
			:itemFilters="menuItemFilters"
			@menuTabClicked="menuTabClicked"
		/>
		<CoworkingMenu
			v-else-if="menuToShow == menuComponents.COWORKING"
			:menuTabs="menuTabs"
			:itemFilters="menuItemFilters"
			@menuTabClicked="menuTabClicked"
		/>
		<AirportMenu
			v-else-if="menuToShow == menuComponents.AIRPORT"
			:menuTabs="menuTabs"
			:itemFilters="menuItemFilters"
			@menuTabClicked="menuTabClicked"
		/>
		<DefaultMenu
			v-else-if="menuToShow == menuComponents.DEFAULT"
			:menuTabs="menuTabs"
			:itemFilters="menuItemFilters"
			@menuTabClicked="menuTabClicked"
		/>
		<loading v-else :active="true" />
		<PreorderOnlyModal v-if="preorderOnly" ref="timeModal" />
		<DeliveryTimeModal
			v-else
			ref="timeModal"
			@confirm="confirmDateChange"
		/>
		<ConfirmationModal ref="confirmationModal" />
	</div>
</template>

<style scoped>
.menu-view-container {
	min-height: 100%;
	height: 100%;
}
</style>

<script>
import { BusinessType } from "@reef-os/utils/enums/businessType"
import { MenuType } from "@reef-os/utils/enums/menuType"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import Loading from "vue-loading-overlay"
import DefaultMenu from "@/components/menu/DefaultMenu.vue"
import HotelMenu from "@/components/menu/HotelMenu.vue"
import CoworkingMenu from "@/components/menu/CoworkingMenu.vue"
import ResidentialMenu from "@/components/menu/ResidentialMenu.vue"
import EventMenu from "@/components/menu/EventMenu.vue"
import AirportMenu from "@/components/menu/AirportMenu.vue"
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue"
import DeliveryTimeModal from "@/components/modals/DeliveryTimeModal.vue"
import PreorderOnlyModal from "@/components/modals/PreorderOnlyModal.vue"

const MenuTabId = {
	KITCHEN: 0,
	ESSENTIALS: 1
}

const MenuComponent = {
	DEFAULT: "DEFAULT",
	HOTEL: "HOTEL",
	COWORKING: "COWORKING",
	AIRPORT: "AIRPORT",
	RESIDENTIAL: "RESIDENTIAL",
	EVENT: "EVENT"
}

export default {
	name: "Menu",
	components: {
		DefaultMenu,
		HotelMenu,
		CoworkingMenu,
		ResidentialMenu,
		EventMenu,
		AirportMenu,
		Loading,
		ConfirmationModal,
		DeliveryTimeModal,
		PreorderOnlyModal
	},
	data: function () {
		return {
			mounted: false,
			menuTabId: MenuTabId.KITCHEN,
			menuItemFilters: {
				menuTypes: [
					MenuType.DEFAULT,
					MenuType.POPUP,
					MenuType.EMBEDDED
				],
				brandId: null
			},
			menuComponents: MenuComponent
		}
	},
	computed: {
		business() {
			return this.$store.state.business
		},
		showEventMenu() {
			return (
				this.businessesDataLoaded && this.$route.meta.isEvent === true
			)
		},
		businessesDataLoaded() {
			return (
				this.$route.params.slug == this.$store.state.business.slug &&
				this.mounted &&
				!this.$store.state.loading
			)
		},
		menuToShow() {
			if (!this.businessesDataLoaded) {
				return null
			}

			if (this.showEventMenu) {
				return MenuComponent.EVENT
			}

			switch (this.business.type) {
				case BusinessType.RESIDENTIAL: {
					return MenuComponent.RESIDENTIAL
				}
				case BusinessType.HOTEL: {
					return MenuComponent.HOTEL
				}
				case BusinessType.COWORKING: {
					return MenuComponent.COWORKING
				}
				case BusinessType.AIRPORT: {
					return MenuComponent.AIRPORT
				}
				default: {
					return MenuComponent.DEFAULT
				}
			}
		},
		menuTabs() {
			let tabs = []

			switch (this.menuToShow) {
				case MenuComponent.DEFAULT:
				case MenuComponent.HOTEL:
				case MenuComponent.COWORKING: {
					let kitchenTabEnabled = false
					let essentialsTabEnabled = false

					for (
						let i = 0;
						i < this.$store.getters.inStockMenus.length;
						i++
					) {
						let menu = this.$store.getters.inStockMenus[i]
						switch (menu.type) {
							case MenuType.ESSENTIALS: {
								essentialsTabEnabled = true
								break
							}
							default: {
								kitchenTabEnabled = true
								break
							}
						}
						if (essentialsTabEnabled && kitchenTabEnabled) {
							break
						}
					}

					if (kitchenTabEnabled) {
						tabs.push({
							id: MenuTabId.KITCHEN,
							text: `<i class="sk-icon-utensils-solid"></i> Kitchen`
						})
					}

					if (essentialsTabEnabled) {
						tabs.push({
							id: MenuTabId.ESSENTIALS,
							text: `<i class="sk-icon-store-solid"></i> Essentials`
						})
					}
					break
				}
				case MenuComponent.AIRPORT: {
					tabs = this.$store.state.brands.map((brand) => {
						let brandItems = this.$store.state.menu.filter(
							(item) => {
								const menuBrandId =
									this.$store.getters.menuBrandsMap[
										item.menu_id
									]
								return menuBrandId != undefined
									? menuBrandId == brand.id
									: false
							}
						)
						return {
							id: brand.id,
							text: brand.name,
							quantity: brandItems.length
						}
					})
					break
				}
			}

			tabs.forEach((tab) => {
				tab.selected = this.menuTabId == tab.id
			})

			return tabs
		},
		preorderOnly() {
			return this.$store.getters.preorderOnly
		}
	},
	created: function () {
		eventBus.on(EventBusEvents.OPEN_MENU_TIME_MODAL, this.openMenuTimeModal)
	},
	mounted: function () {
		this.mounted = true
		this.$store.dispatch("initHome", this.$route.params.slug)
	},
	methods: {
		openMenuTimeModal(options) {
			this.$refs.timeModal.open(options)
		},
		clearUserDataIfNeeded() {
			if (
				this.$store.getters.inStockMenus.some(
					(menu) => menu.type == MenuType.EMBEDDED
				)
			) {
				this.$store.dispatch("clearUserData")
			}
		},
		menuTabClicked(tab) {
			if (this.menuTabId == tab.id) {
				return
			}

			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Menu Tab Clicked",
				data: {
					tabName: tab.text,
					fromDeeplink: tab.deeplink
				}
			})
			this.setMenuTab(tab.id)
		},
		setMenuTab(tabId) {
			this.menuTabId = tabId
			if (this.menuToShow == MenuComponent.AIRPORT) {
				this.menuItemFilters.brandId = tabId
				this.menuItemFilters.menuTypes = null
			} else {
				switch (tabId) {
					case MenuTabId.KITCHEN: {
						this.menuItemFilters.menuTypes = [
							MenuType.DEFAULT,
							MenuType.POPUP,
							MenuType.EMBEDDED
						]
						break
					}
					case MenuTabId.ESSENTIALS: {
						this.menuItemFilters.menuTypes = [MenuType.ESSENTIALS]
					}
				}
			}
		},
		async confirmDateChange(options) {
			try {
				const confirmed = await this.$refs.confirmationModal.open({
					title: "Change Pre Order Setting?",
					body: "Changing the Pre Order Date or Time will clear your Cart. Are you sure you want to continue?",
					cancel: "Keep Set Time & Date",
					confirmation: "Continue"
				})
				options.confirmed(confirmed)
			} catch (e) {
				options.confirmed(false)
			}
		}
	},
	watch: {
		businessesDataLoaded(loaded) {
			if (loaded) {
				this.clearUserDataIfNeeded()
			}
		},
		$route(to, from) {
			if (to.meta.isMenu && to.name != from.name) {
				this.$store.dispatch("initHome", this.$route.params.slug)
			}
		},
		menuTabs() {
			if (this.menuTabs && this.menuTabs.length > 0) {
				const selectedTab = this.menuTabs.find(
					(tab) => tab.id == this.menuTabId
				)
				if (!selectedTab) {
					this.setMenuTab(this.menuTabs[0].id)
				}
			} else {
				this.setMenuTab(MenuTabId.KITCHEN)
			}
		}
	},
	destroyed: function () {
		eventBus.off(
			EventBusEvents.OPEN_MENU_TIME_MODAL,
			this.openMenuTimeModal
		)
	}
}
</script>

